import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Button, Box, Typography, Grid } from '@mui/material';
import Close from "../../../../img/close.png";
import SmartTalkIcon from "../../../../img/machine-learning.png";
import { usePallet } from '../../../../contexts/PalletContext';
import UsuarioService from '../../../../services/UsuarioService';
import { useAlert } from "../../../../contexts/AlertContext";


interface SmartTalkModalContextoProps {
    open: boolean;
    onClose: () => void;
    id_usuario: number;
}

type SmartTalkContexto = {
    porteRamoPosicionamento?: string;
    produtoServico?: string;
    perfisClientesFaturamentoSetor?: string;
    outrasObservacoes?: string;
};


type Message = { from: 'user' | 'bot'; text: string };

const gradient = 'linear-gradient(45deg, rgba(50, 35, 187, 1), rgba(231, 62, 203, 1))';

const SmartTalkModalContexto: React.FC<SmartTalkModalContextoProps> = ({ open, onClose, id_usuario }) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const { pallet } = usePallet();
    const [porteRamoPosicionamento, setPorteRamoPosicionamento] = useState('');
    const [produtoServico, setProdutoServico] = useState('');
    const [perfisClientesFaturamentoSetor, setPerfisClientesFaturamentoSetor] = useState('');
    const [outrasObservacoes, setOutrasObservacoes] = useState('');
    const [saving, setSaving] = useState(false);
    const { NewAlert } = useAlert();


    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const resContexto = await UsuarioService.GetSmartTalkContext(id_usuario)
                const raw = resContexto?.[0]?.smart_talk_contexto;
                const ctx: SmartTalkContexto = (typeof raw === 'string' ? JSON.parse(raw) : raw) ?? {};

                if (ctx) {
                    setPorteRamoPosicionamento(ctx?.porteRamoPosicionamento);
                    setProdutoServico(ctx?.produtoServico);
                    setPerfisClientesFaturamentoSetor(ctx?.perfisClientesFaturamentoSetor);
                    setOutrasObservacoes(ctx?.outrasObservacoes);
                }
            } catch {

            }
        })();
    }, [open]);

    const handleSaveContext = async () => {
        if (!porteRamoPosicionamento.trim() && !produtoServico.trim() && !perfisClientesFaturamentoSetor.trim() && !outrasObservacoes.trim()) {
            setMessages(prev => [
                ...prev,
                { from: 'bot', text: 'Por favor, preencha ao menos um dos campos antes de salvar.' }
            ]);
            return;
        }

        setSaving(true);

        const data = JSON.stringify({
            porteRamoPosicionamento,
            produtoServico,
            perfisClientesFaturamentoSetor,
            outrasObservacoes
        })

        try {
            const res = await UsuarioService
                .UpdateSmartTalkContext(id_usuario, data)
                .catch(err => console.error('Erro ao salvar contexto do Smart Talk no DB:', err));

            if (!res) NewAlert('error', 'Não foi possível salvar o contexto agora. Tente novamente em instantes.');
            if (res) NewAlert('success', 'Contexto salvo! Vamos usar essas informações para personalizar sua experiência.')


            // VAMOS limpar os campos assim que finalizar todo o fluxo...
            // setPorteRamoPosicionamento('');
            // setProdutoServico('');
            // setPerfisClientesFaturamentoSetor('');
        } catch {
            setMessages(prev => [
                ...prev,
                { from: 'bot', text: 'Não foi possível salvar o contexto agora. Tente novamente em instantes.' }
            ]);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{ style: { backgroundColor: '#1e1e1e', color: '#fff', borderRadius: '7px' } }}
        >
            <DialogTitle sx={{ m: 0, p: 3 }}>
                <Grid container>
                    <Grid>
                        <img
                            src={SmartTalkIcon}
                            alt="SmartTalkIcon"
                            style={{ width: 37, marginRight: 10 }}
                        />
                    </Grid>
                    <Grid style={{ marginTop: '5px', fontFamily: `${pallet.general.fontFamily}`, fontWeight: pallet.general.weight_600 }}>
                        Smart Talk - Contextualizando
                    </Grid>
                </Grid>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}
                >
                    <img
                        src={Close}
                        alt="Close"
                        style={{ width: 40, marginRight: 5, paddingTop: '7px' }}
                    />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    maxHeight: '60vh',
                    overflowY: 'auto',
                    p: 5,

                    // Força família e peso 100 em tudo
                    fontFamily: `${pallet.general.fontFamily} !important`,
                    fontWeight: pallet.general.weight_100,

                    '& *': {
                        fontFamily: `${pallet.general.fontFamily} !important`,
                        fontWeight: `${pallet.general.weight_100} !important`,
                    },

                    // Targets que realmente desenham texto no MUI
                    '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-label': {
                        fontFamily: `${pallet.general.fontFamily} !important`,
                        fontWeight: `${pallet.general.weight_100} !important`,
                    },
                    '& .MuiFormLabel-root': {
                        fontFamily: `${pallet.general.fontFamily} !important`,
                        fontWeight: `${pallet.general.weight_100} !important`,
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: `${pallet.general.fontFamily} !important`,
                        fontWeight: `${pallet.general.weight_100} !important`,
                        '&::placeholder': {
                            fontWeight: `${pallet.general.weight_100} !important`,
                            opacity: 1,
                        },
                    },

                    // Scrollbar dark e fina
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#444 #1e1e1e',
                    '&::-webkit-scrollbar': { width: '6px', height: '6px' },
                    '&::-webkit-scrollbar-track': { background: '#1e1e1e' },
                    '&::-webkit-scrollbar-thumb': { background: '#444', borderRadius: '8px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                }}
            >
                {/* --- INTRODUÇÃO CURTA --- */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: '#cfcfcf', fontFamily: `${pallet.general.fontFamily}` }}
                    >
                        Este é o espaço para apresentar a sua empresa, suas intenções e informações-chave.
                        Assim, o agente passa a te conhecer melhor e consegue ajudar de forma cada vez mais personalizada.
                    </Typography>
                </Box>

                {/* --- FORM DARK --- */}
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            label="1. Descreva o seu porte como empresa, o ramo de atuação e como se posiciona no mercado."
                            value={porteRamoPosicionamento}
                            onChange={(e) => setPorteRamoPosicionamento(e.target.value)}
                            variant="filled"
                            fullWidth
                            multiline
                            minRows={3}
                            // [AJUSTE 2] Focus com gradiente no underline e label
                            InputProps={{
                                sx: {
                                    backgroundColor: '#333',
                                    color: '#fff',
                                    caretColor: '#fff',
                                    '&:before': { borderBottomColor: '#555 !important' },
                                    '&:hover:before': { borderBottomColor: '#777 !important' },
                                    '&.Mui-focused:before': { borderBottomColor: 'transparent !important' },
                                    '&.Mui-focused:after': {
                                        borderBottom: 'none',
                                        height: '2px',
                                        background: gradient,
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        transform: 'none',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#9e9e9e',
                                    fontWeight: 100,
                                    '&.Mui-focused': {
                                        color: 'transparent',
                                        backgroundImage: gradient,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    },
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="2. Diga que tipo de produto ou serviço você entrega."
                            value={produtoServico}
                            onChange={(e) => setProdutoServico(e.target.value)}
                            variant="filled"
                            fullWidth
                            multiline
                            minRows={3}
                            InputProps={{
                                sx: {
                                    backgroundColor: '#333',
                                    color: '#fff',
                                    caretColor: '#fff',
                                    '&:before': { borderBottomColor: '#555 !important' },
                                    '&:hover:before': { borderBottomColor: '#777 !important' },
                                    '&.Mui-focused:before': { borderBottomColor: 'transparent !important' },
                                    '&.Mui-focused:after': {
                                        borderBottom: 'none',
                                        height: '2px',
                                        background: gradient,
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        transform: 'none',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#9e9e9e',
                                    '&.Mui-focused': {
                                        color: 'transparent',
                                        backgroundImage: gradient,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    },
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="3. Quais perfis de clientes você atende, nível de faturamento e o seu respectivo setor de atuação."
                            value={perfisClientesFaturamentoSetor}
                            onChange={(e) => setPerfisClientesFaturamentoSetor(e.target.value)}
                            variant="filled"
                            fullWidth
                            multiline
                            minRows={3}
                            InputProps={{
                                sx: {
                                    backgroundColor: '#333',
                                    color: '#fff',
                                    caretColor: '#fff',
                                    '&:before': { borderBottomColor: '#555 !important' },
                                    '&:hover:before': { borderBottomColor: '#777 !important' },
                                    '&.Mui-focused:before': { borderBottomColor: 'transparent !important' },
                                    '&.Mui-focused:after': {
                                        borderBottom: 'none',
                                        height: '2px',
                                        background: gradient,
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        transform: 'none',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#9e9e9e',
                                    '&.Mui-focused': {
                                        color: 'transparent',
                                        backgroundImage: gradient,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    },
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="4. Outras observações relevantes."
                            value={outrasObservacoes}
                            onChange={(e) => setOutrasObservacoes(e.target.value)}
                            variant="filled"
                            fullWidth
                            multiline
                            minRows={3}
                            InputProps={{
                                sx: {
                                    backgroundColor: '#333',
                                    color: '#fff',
                                    caretColor: '#fff',
                                    '&:before': { borderBottomColor: '#555 !important' },
                                    '&:hover:before': { borderBottomColor: '#777 !important' },
                                    '&.Mui-focused:before': { borderBottomColor: 'transparent !important' },
                                    '&.Mui-focused:after': {
                                        borderBottom: 'none',
                                        height: '2px',
                                        background: gradient,
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        transform: 'none',
                                    },
                                }
                            }}
                            InputLabelProps={{
                                sx: {
                                    color: '#9e9e9e',
                                    '&.Mui-focused': {
                                        color: 'transparent',
                                        backgroundImage: gradient,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                    },
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                {/* --- HISTÓRICO (mantido) --- */}
                <Box sx={{ mt: 2 }}>
                    {messages.map((msg, idx) => (
                        <Box key={idx} sx={{ mb: 1, textAlign: msg.from === 'user' ? 'right' : 'left' }}>
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{
                                    display: 'inline-block',
                                    p: 1,
                                    borderRadius: 1,
                                    backgroundColor: msg.from === 'user' ? '#333' : '#2a2a2a',
                                    fontFamily: `${pallet.general.fontFamily}`,
                                    color: '#fff',
                                }}
                            >
                                {msg.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#2a2a2a' }}>
                <Grid onClick={onClose} sx={{ ml: 1 }}>
                    <Button
                        style={{
                            background: gradient,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontWeight: 'bold'
                        }}
                        variant="text"
                    >
                        Cancelar
                    </Button>
                </Grid>

                <Grid onClick={handleSaveContext} sx={{ ml: 1 }}>
                    <Button
                        disabled={saving}
                        style={{
                            background: gradient,
                            color: 'white',
                            border: 'none'
                        }}
                        variant="outlined"
                    >
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

export default SmartTalkModalContexto;